import React from "react";
import { Calendar, Mail, Video } from "lucide-react";
import { WQE } from "@/config/weeklyqe";

const Prose = ({ text, className = "" }) => {
  const paragraphs = text.split(/\n\n+/);
  const items = [];
  paragraphs.forEach((paragraph, pi) => {
    const sentences = paragraph.trim().split(/(?<=[.?!])\s+(?=["'A-Z])/);
    sentences.forEach((sentence, si) => {
      const isFirstOfLaterParagraph = pi > 0 && si === 0;
      items.push(
        <p key={`${pi}-${si}`} className={`${className} ${isFirstOfLaterParagraph ? "mt-7" : ""}`}>
          {sentence.split("\n").map((line, k, arr) => (
            <React.Fragment key={k}>
              {line}
              {k < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    });
  });
  return items;
};

const stepIcons = [Mail, Calendar, Video];

export default function WeeklyQEWelcome() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="wqe-welcome-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-0 md:h-16 flex flex-col md:flex-row items-center md:justify-between gap-2 md:gap-4">
          <a
            href="#top"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="flex items-center gap-3"
            data-testid="wqe-welcome-brand"
          >
            <img src={WQE.logoUrl} alt={WQE.parentBrand} className="h-8 md:h-9 w-auto" />
          </a>
          <span className="text-[11px] md:text-xs uppercase tracking-[0.22em] md:tracking-[0.24em] text-muted-foreground">
            Weekly Quantum Edge
          </span>
        </div>
      </header>

      {/* Welcome */}
      <section id="top" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-10">
        <p className="text-xs uppercase tracking-[0.24em] text-brand-turquoise">Welcome</p>
        <h1
          className="font-serif text-4xl md:text-5xl tracking-tight mt-5 leading-[1.1]"
          data-testid="wqe-welcome-heading"
        >
          {WQE.welcome.headline}
        </h1>
        <div className="mt-8 md:mt-10 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]">
          <Prose text={WQE.welcome.body} />
        </div>
      </section>

      {/* Next steps */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="wqe-next-steps">
        <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
          {WQE.welcome.nextStepsHeading}
        </p>
        <div className="mt-8 grid grid-cols-1 gap-6">
          {WQE.welcome.steps.map((step, i) => {
            const Icon = stepIcons[i] || Mail;
            return (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-6 md:p-8 flex gap-5 items-start"
                data-testid={`wqe-step-${i}`}
              >
                <div className="h-10 w-10 rounded-full bg-brand-turquoise/12 grid place-items-center shrink-0">
                  <Icon className="h-5 w-5 text-brand-turquoise" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight">
                    {`${i + 1}. ${step.label}`}
                  </h3>
                  <div className="mt-2 space-y-3 text-[15px] md:text-base leading-[1.7] text-foreground/80">
                    <Prose text={step.body} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* What to expect */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="wqe-what-to-expect">
        <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
          {WQE.welcome.expectHeading}
        </p>
        <div className="mt-6 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]">
          <Prose text={WQE.welcome.expectBody} />
        </div>
      </section>

      {/* Personal welcome */}
      <section
        className="border-t border-border/40 bg-muted/30 mt-10"
        data-testid="wqe-personal"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="text-xs uppercase tracking-[0.24em] text-brand-turquoise">
            {WQE.welcome.personalHeading}
          </p>
          <div className="mt-6 space-y-4 font-serif text-xl md:text-2xl italic text-foreground/85 leading-relaxed max-w-[52ch]">
            <Prose text={WQE.welcome.personalBody} />
          </div>
          <p className="mt-10 text-sm text-muted-foreground">{WQE.welcome.signoff}</p>
        </div>
      </section>

      <footer className="border-t border-border/40">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-8 text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} {WQE.parentBrand} · Thank you for being here.
        </div>
      </footer>
    </div>
  );
}
